/**
 * FilterBuilder Component
 *
 * Advanced filtering UI for UDC tables
 * - Select field from available_filters
 * - Select operator based on field type
 * - Dynamic value input based on operator
 * - Support for AND/OR logic between filters
 */

import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Button,
  Space,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Switch,
  Divider,
  Alert,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import type {
  UDCFilter,
  FilterOperator,
  AvailableFilter,
} from '@/api/types/udc.types';
import './FilterBuilder.css';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

interface FilterBuilderProps {
  open: boolean;
  onClose: () => void;
  availableFilters: AvailableFilter[];
  activeFilters: UDCFilter[];
  onApply: (filters: UDCFilter[], usage?: string) => void;
}

interface FilterRow {
  id: string;
  column: string;
  operator: FilterOperator;
  value: any;
}

export const FilterBuilder: React.FC<FilterBuilderProps> = ({
  open,
  onClose,
  availableFilters,
  activeFilters,
  onApply,
}) => {
  // Initialize with active filters or empty state
  const [filterRows, setFilterRows] = useState<FilterRow[]>(() => {
    if (activeFilters.length > 0) {
      return activeFilters.map((f) => ({
        id: f.id,
        column: f.column,
        operator: f.operator,
        value: f.value,
      }));
    }
    return [createEmptyFilter()];
  });

  const [filterLogic, setFilterLogic] = useState<'AND' | 'OR'>('AND');

  // Create empty filter row
  function createEmptyFilter(): FilterRow {
    return {
      id: `f${Date.now()}`,
      column: '',
      operator: 'equals' as FilterOperator,
      value: null,
    };
  }

  // Add new filter row
  const handleAddFilter = () => {
    setFilterRows([...filterRows, createEmptyFilter()]);
  };

  // Remove filter row
  const handleRemoveFilter = (id: string) => {
    if (filterRows.length === 1) {
      // Reset to empty filter instead of removing last one
      setFilterRows([createEmptyFilter()]);
    } else {
      setFilterRows(filterRows.filter((f) => f.id !== id));
    }
  };

  // Update filter row
  const updateFilter = (id: string, updates: Partial<FilterRow>) => {
    setFilterRows(
      filterRows.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );
  };

  // Get available operators for field
  const getOperatorsForField = (columnName: string): FilterOperator[] => {
    const field = availableFilters.find((f) => f.name === columnName);
    if (!field) return ['equals' as FilterOperator];

    // Map field type to operators
    switch (field.type) {
      case 'string':
      case 'text':
        return [
          'equals',
          'not_equals',
          'contains',
          'starts_with',
          'ends_with',
          'is_null',
          'is_not_null',
        ] as FilterOperator[];

      case 'int':
      case 'float':
      case 'decimal':
        return [
          'equals',
          'not_equals',
          'greater_than',
          'less_than',
          'greater_than_or_equal',
          'less_than_or_equal',
          'between',
          'is_null',
          'is_not_null',
        ] as FilterOperator[];

      case 'date':
      case 'datetime':
        return [
          'equals',
          'not_equals',
          'greater_than',
          'less_than',
          'between',
          'on_date',
          'is_null',
          'is_not_null',
        ] as FilterOperator[];

      case 'boolean':
        return ['equals', 'is_null', 'is_not_null'] as FilterOperator[];

      case 'lookup':
      case 'lookup2':
      case 'enum':
        return [
          'equals',
          'not_equals',
          'in',
          'not_in',
          'is_null',
          'is_not_null',
        ] as FilterOperator[];

      default:
        return [
          'equals',
          'not_equals',
          'is_null',
          'is_not_null',
        ] as FilterOperator[];
    }
  };

  // Get field type
  const getFieldType = (columnName: string): string => {
    const field = availableFilters.find((f) => f.name === columnName);
    return field?.type || 'string';
  };

  // Get lookup options for field
  const getLookupOptions = (
    columnName: string
  ): Array<{ label: string; value: any }> => {
    const field = availableFilters.find((f) => f.name === columnName);
    if (!field || !field.lookup_options) return [];

    return field.lookup_options.map((opt) => ({
      label: opt.display_name || opt.value,
      value: opt.value,
    }));
  };

  // Render value input based on operator and field type
  const renderValueInput = (filter: FilterRow) => {
    const fieldType = getFieldType(filter.column);
    const operator = filter.operator;

    // No input needed for these operators
    if (operator === 'is_null' || operator === 'is_not_null') {
      return null;
    }

    // Between operator - two inputs
    if (operator === 'between') {
      if (fieldType === 'date' || fieldType === 'datetime') {
        return (
          <RangePicker
            value={
              filter.value
                ? [dayjs(filter.value[0]), dayjs(filter.value[1])]
                : null
            }
            onChange={(dates) => {
              if (dates) {
                updateFilter(filter.id, {
                  value: [dates[0]?.toISOString(), dates[1]?.toISOString()],
                });
              }
            }}
            style={{ width: '100%' }}
          />
        );
      } else {
        // Number range
        return (
          <Space.Compact style={{ width: '100%' }}>
            <InputNumber
              placeholder="From"
              value={filter.value?.[0]}
              onChange={(val) =>
                updateFilter(filter.id, { value: [val, filter.value?.[1]] })
              }
              style={{ width: '50%' }}
            />
            <InputNumber
              placeholder="To"
              value={filter.value?.[1]}
              onChange={(val) =>
                updateFilter(filter.id, { value: [filter.value?.[0], val] })
              }
              style={{ width: '50%' }}
            />
          </Space.Compact>
        );
      }
    }

    // In/Not In operators - multi-select
    if (operator === 'in' || operator === 'not_in') {
      const options = getLookupOptions(filter.column);
      if (options.length > 0) {
        return (
          <Select
            mode="multiple"
            placeholder="Select values"
            value={filter.value}
            onChange={(val) => updateFilter(filter.id, { value: val })}
            options={options}
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      } else {
        return (
          <Select
            mode="tags"
            placeholder="Type and press Enter"
            value={filter.value}
            onChange={(val) => updateFilter(filter.id, { value: val })}
            style={{ width: '100%' }}
          />
        );
      }
    }

    // Field type specific inputs
    switch (fieldType) {
      case 'boolean':
        return (
          <Select
            value={filter.value}
            onChange={(val) => updateFilter(filter.id, { value: val })}
            style={{ width: '100%' }}
            options={[
              { label: 'True', value: true },
              { label: 'False', value: false },
            ]}
          />
        );

      case 'date':
      case 'datetime':
        return (
          <DatePicker
            value={filter.value ? dayjs(filter.value) : null}
            onChange={(date) =>
              updateFilter(filter.id, { value: date?.toISOString() })
            }
            style={{ width: '100%' }}
            showTime={fieldType === 'datetime'}
          />
        );

      case 'int':
      case 'float':
      case 'decimal':
        return (
          <InputNumber
            value={filter.value}
            onChange={(val) => updateFilter(filter.id, { value: val })}
            style={{ width: '100%' }}
            placeholder="Enter number"
          />
        );

      case 'lookup':
      case 'lookup2':
      case 'enum': {
        const options = getLookupOptions(filter.column);
        if (options.length > 0) {
          return (
            <Select
              value={filter.value}
              onChange={(val) => updateFilter(filter.id, { value: val })}
              options={options}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          );
        }
      }
      // Fallthrough to text input if no options

      default:
        return (
          <Input
            value={filter.value}
            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
            placeholder="Enter value"
          />
        );
    }
  };

  // Apply filters
  const handleApply = () => {
    // Filter out incomplete filters
    const validFilters = filterRows.filter(
      (f) =>
        f.column &&
        f.operator &&
        ((f.value !== null && f.value !== undefined && f.value !== '') ||
          f.operator === 'is_null' ||
          f.operator === 'is_not_null')
    );

    if (validFilters.length === 0) {
      onApply([], undefined);
      onClose();
      return;
    }

    // Convert to UDCFilter format
    const udcFilters: UDCFilter[] = validFilters.map((f) => ({
      id: f.id,
      column: f.column,
      operator: f.operator,
      value: f.value,
    }));

    // Build usage string (e.g., "f1 and f2 and f3")
    const usage = validFilters
      .map((f) => f.id)
      .join(` ${filterLogic.toLowerCase()} `);

    onApply(udcFilters, usage);
    onClose();
  };

  // Clear all filters
  const handleClear = () => {
    setFilterRows([createEmptyFilter()]);
    onApply([], undefined);
    onClose();
  };

  // Get operator label
  const getOperatorLabel = (op: FilterOperator): string => {
    const labels: Record<string, string> = {
      equals: 'Equals',
      not_equals: 'Not Equals',
      greater_than: 'Greater Than',
      less_than: 'Less Than',
      greater_than_or_equal: 'Greater or Equal',
      less_than_or_equal: 'Less or Equal',
      contains: 'Contains',
      starts_with: 'Starts With',
      ends_with: 'Ends With',
      in: 'In',
      not_in: 'Not In',
      between: 'Between',
      on_date: 'On Date',
      is_null: 'Is Empty',
      is_not_null: 'Is Not Empty',
    };
    return labels[op] || op;
  };

  return (
    <Drawer
      title={
        <Space>
          <FilterOutlined />
          <span>Advanced Filters</span>
        </Space>
      }
      placement="right"
      width={600}
      open={open}
      onClose={onClose}
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button onClick={handleClear}>Clear All</Button>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={handleApply}>
              Apply Filters
            </Button>
          </Space>
        </Space>
      }
    >
      {availableFilters.length === 0 ? (
        <Alert
          message="No Filterable Fields"
          description="This table does not support filtering."
          type="info"
          showIcon
        />
      ) : (
        <>
          {/* Filter Logic Toggle */}
          {filterRows.length > 1 && (
            <div className="filter-logic-toggle">
              <span>Match:</span>
              <Select
                value={filterLogic}
                onChange={setFilterLogic}
                style={{ width: 120, marginLeft: 8 }}
                options={[
                  { label: 'All (AND)', value: 'AND' },
                  { label: 'Any (OR)', value: 'OR' },
                ]}
              />
            </div>
          )}

          <Divider />

          {/* Filter Rows */}
          <div className="filter-rows">
            {filterRows.map((filter, index) => (
              <div key={filter.id} className="filter-row">
                <Row gutter={[8, 8]} align="middle">
                  {/* Field Selector */}
                  <Col span={24}>
                    <Select
                      placeholder="Select field"
                      value={filter.column || undefined}
                      onChange={(val) => {
                        updateFilter(filter.id, {
                          column: val,
                          operator: getOperatorsForField(val)[0],
                          value: null,
                        });
                      }}
                      style={{ width: '100%' }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {availableFilters.map((field) => (
                        <Select.Option key={field.name} value={field.name}>
                          {field.display_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>

                  {/* Operator Selector */}
                  <Col span={24}>
                    <Select
                      placeholder="Operator"
                      value={filter.operator}
                      onChange={(val) =>
                        updateFilter(filter.id, { operator: val, value: null })
                      }
                      style={{ width: '100%' }}
                      disabled={!filter.column}
                    >
                      {getOperatorsForField(filter.column).map((op) => (
                        <Select.Option key={op} value={op}>
                          {getOperatorLabel(op)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>

                  {/* Value Input */}
                  <Col span={22}>{renderValueInput(filter)}</Col>

                  {/* Remove Button */}
                  <Col span={2}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveFilter(filter.id)}
                      disabled={filterRows.length === 1 && !filter.column}
                    />
                  </Col>
                </Row>

                {index < filterRows.length - 1 && (
                  <div className="filter-logic-label">{filterLogic}</div>
                )}
              </div>
            ))}
          </div>

          {/* Add Filter Button */}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddFilter}
            style={{ width: '100%', marginTop: 16 }}
          >
            Add Filter
          </Button>
        </>
      )}
    </Drawer>
  );
};
